import { useAuthStore } from 'src/stores/auth'
import { useI18n } from 'vue-i18n'
import { ISex } from 'src/graphql'
import { watch } from 'vue'
import { i18n } from 'boot/i18n'

// Platform identifier for cross-app analytics
const PLATFORM = 'client_app'

// Declare dataLayer on window
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[]
  }
}

/**
 * Push event to dataLayer with platform automatically included.
 * GTM will pick up these events and route them to GA4, Meta Pixel, etc.
 */
const pushEvent = (eventName: string, params?: Record<string, unknown>) => {
  if (typeof window === 'undefined') return

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: eventName,
    platform: PLATFORM,
    ...params,
  })
}

export interface ArticleEngagementParams {
  slug: string
  title: string
  label?: string
}

export interface ArticleSharedParams extends ArticleEngagementParams {
  share_method: 'whatsapp' | 'email' | 'facebook' | 'copy_link' | 'other'
}

export interface TripEngagementParams {
  id: string
  title: string
  label?: string
  area?: string
}

export interface TripSharedParams extends TripEngagementParams {
  share_method: 'whatsapp' | 'email' | 'facebook' | 'copy_link' | 'other'
}

export interface TripFilterAppliedParams {
  filter_type: string
  filter_value: string
  results_count?: number
}

export interface SignUpParams {
  method: 'google' | 'email' | 'facebook' | 'apple'
}

export interface SignInParams {
  method: 'google' | 'email' | 'facebook' | 'apple'
}

export interface ProfileCompletedParams {
  completion_percentage: number
}

export interface TripReviewSubmittedParams {
  id: string
  title: string
  rating: number
}

export interface PWAInstalledParams {
  install_source: 'banner' | 'menu' | 'share'
}

export interface ArticleRecommendationClickedParams {
  location: 'article_detail'
  position?: number
  fromSlug: string
  fromTitle: string
  toSlug: string
  toTitle: string
}

export interface TripRecommendationClickedParams {
  location: 'trip_detail'
  position?: number
  fromId: string
  fromTitle: string
  toId: string
  toTitle: string
}

// Enhanced tracking parameters interface
export interface EnhancedAnalyticsParams {
  platform: 'web' | 'client_app'
  sex?: 'male' | 'female' | 'unknown'
  user_type: 'guest' | 'registered' | 'premium'
  device_type: 'mobile' | 'tablet' | 'desktop'
  app_version: string
  content_language: string
  time_of_day: 'morning' | 'afternoon' | 'evening' | 'night'
  day_of_week: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'
  pwa_installed: boolean
}

const DAY_OF_WEEK_MAP: Record<string, string> = {
  '0': 'sunday',
  '1': 'monday',
  '2': 'tuesday',
  '3': 'wednesday',
  '4': 'thursday',
  '5': 'friday',
  '6': 'saturday',
}

export const useAnalytics = () => {
  // Helpers to build GA4-friendly items
  const buildArticleItem = (p: ArticleEngagementParams) => ({
    item_id: p.slug,
    item_name: p.title,
    item_category: 'article',
    item_variant: p.label,
  })
  const buildTripItem = (p: TripEngagementParams) => ({
    item_id: p.id,
    item_name: p.title,
    item_category: 'trip',
    item_variant: p.label,
    item_brand: p.area,
  })

  // Article Content Engagement Events
  const trackArticleViewed = (params: ArticleEngagementParams) => {
    pushEvent('view_item', {
      content_type: 'article',
      items: [buildArticleItem(params)],
    })
  }

  const trackArticleLiked = (params: ArticleEngagementParams) => {
    pushEvent('add_to_wishlist', {
      content_type: 'article',
      items: [buildArticleItem(params)],
    })
  }

  const trackArticleShared = (params: ArticleSharedParams) => {
    pushEvent('share', {
      content_type: 'article',
      method: params.share_method,
      items: [buildArticleItem(params)],
    })
  }

  const trackArticleSaved = (params: ArticleEngagementParams) => {
    pushEvent('add_to_wishlist', {
      content_type: 'article',
      items: [buildArticleItem(params)],
    })
  }

  const trackArticleRecommendationClicked = (params: ArticleRecommendationClickedParams) => {
    pushEvent('select_item', {
      item_list_name: params.location,
      index: params.position,
      items: [
        {
          item_id: params.toSlug,
          item_name: params.toTitle,
          item_category: 'article',
        },
      ],
      from_item_id: params.fromSlug,
      from_item_name: params.fromTitle,
    })
  }

  // Trip Content Engagement Events
  const trackTripViewed = (params: TripEngagementParams) => {
    pushEvent('view_item', {
      content_type: 'trip',
      items: [buildTripItem(params)],
    })
  }

  const trackTripLiked = (params: TripEngagementParams) => {
    pushEvent('add_to_wishlist', {
      content_type: 'trip',
      items: [buildTripItem(params)],
    })
  }

  const trackTripShared = (params: TripSharedParams) => {
    pushEvent('share', {
      content_type: 'trip',
      method: params.share_method,
      items: [buildTripItem(params)],
    })
  }

  const trackTripFinished = (params: TripEngagementParams) => {
    pushEvent('trip_completed', {
      content_type: 'trip',
      items: [buildTripItem(params)],
    })
  }

  const trackTripRecommendationClicked = (params: TripRecommendationClickedParams) => {
    pushEvent('select_item', {
      item_list_name: params.location,
      index: params.position,
      items: [
        {
          item_id: params.toId,
          item_name: params.toTitle,
          item_category: 'trip',
        },
      ],
      from_item_id: params.fromId,
      from_item_name: params.fromTitle,
    })
  }

  // Content Discovery & Search Events
  const trackTripFilterApplied = (params: TripFilterAppliedParams) => {
    if (params.filter_type === 'search') {
      pushEvent('view_search_results', {
        search_term: params.filter_value,
        results_count: params.results_count,
      })
      return
    }
    pushEvent('apply_filter', {
      content_type: 'trip',
      filter_type: params.filter_type,
      filter_value: params.filter_value,
      results_count: params.results_count,
    })
  }

  // User Journey & Authentication Events
  const trackSignUp = (params: SignUpParams) => {
    pushEvent('sign_up', {
      method: params.method,
    })
  }

  const trackSignIn = (params: SignInParams) => {
    pushEvent('login', {
      method: params.method,
    })
  }

  const trackProfileCompleted = (params: ProfileCompletedParams) => {
    pushEvent('profile_completed', {
      completion_percentage: params.completion_percentage,
    })
  }

  // Content Performance & Quality Events
  const trackTripReviewSubmitted = (params: TripReviewSubmittedParams) => {
    pushEvent('review_submitted', {
      content_type: 'trip',
      rating: params.rating,
      items: [
        {
          item_id: params.id,
          item_name: params.title,
          item_category: 'trip',
        },
      ],
    })
  }

  // PWA & Mobile Features Events
  const trackPWAInstalled = (params: PWAInstalledParams) => {
    pushEvent('add_to_home_screen', {
      source: params.install_source,
    })
  }

  return {
    // Article Content Engagement
    trackArticleViewed,
    trackArticleLiked,
    trackArticleShared,
    trackArticleSaved,
    trackArticleRecommendationClicked,

    // Trip Content Engagement
    trackTripViewed,
    trackTripLiked,
    trackTripShared,
    trackTripFinished,
    trackTripRecommendationClicked,

    // Content Discovery & Search
    trackTripFilterApplied,

    // User Journey & Authentication
    trackSignUp,
    trackSignIn,
    trackProfileCompleted,

    // Content Performance & Quality
    trackTripReviewSubmitted,

    // PWA & Mobile Features
    trackPWAInstalled,
  }
}

export const setAnalyticsUserProperties = (): void => {
  const now = new Date()
  const hour = now.getHours()
  const dayOfWeek = now.getDay()

  let timeOfDay: EnhancedAnalyticsParams['time_of_day'] = 'morning'
  if (hour >= 12 && hour < 17) timeOfDay = 'afternoon'
  else if (hour >= 17 && hour < 21) timeOfDay = 'evening'
  else if (hour >= 21 || hour < 6) timeOfDay = 'night'

  const dayType: EnhancedAnalyticsParams['day_of_week'] = DAY_OF_WEEK_MAP[
    dayOfWeek
  ] as EnhancedAnalyticsParams['day_of_week']

  const userAgent = navigator.userAgent
  let deviceType: EnhancedAnalyticsParams['device_type'] = 'desktop'
  if (/iPad/.test(userAgent)) deviceType = 'tablet'
  else if (/Mobile|Android|iPhone/.test(userAgent)) deviceType = 'mobile'

  const authStore = useAuthStore()
  let userType: EnhancedAnalyticsParams['user_type'] = 'guest'
  if (authStore.isAuthenticated) {
    if (authStore.hasActivePass) {
      userType = 'premium'
    } else {
      userType = 'registered'
    }
  }

  let sex: EnhancedAnalyticsParams['sex']
  if (authStore.me?.sex != null) {
    sex = authStore.me.sex === ISex.M ? 'male' : 'female'
  } else {
    sex = 'unknown'
  }

  const contentLanguage = i18n.global.locale || navigator.language || 'cs'

  // Push user properties to dataLayer
  pushEvent('set_user_properties', {
    user_properties: {
      platform: PLATFORM,
      sex,
      user_type: userType,
      device_type: deviceType,
      content_language: contentLanguage,
      time_of_day: timeOfDay,
      day_of_week: dayType,
      app_version: process.env.APP_VERSION || 'unknown',
      pwa_installed: window.matchMedia?.('(display-mode: standalone)').matches ?? false,
    },
  })
}

export const initAnalyticsUserProperties = () => {
  if (process.env.GTM_ID) {
    const authStore = useAuthStore()
    const { locale } = useI18n()

    // Initial set
    setAnalyticsUserProperties()

    // Update when relevant states change
    watch(
      () => [authStore.isAuthenticated, authStore.hasActivePass, authStore.me?.sex, locale.value],
      () => setAnalyticsUserProperties(),
      { immediate: false },
    )
  }
}
